import "@/utils/scroll";
import { enableMapSet } from "immer";
import { createApp } from "vue";

import { createServices, IServices } from "@/app/di";
import { provideServicesPlugin } from "@/configuration/plugins/app/app-provider.configuration";
import { enterpriseSentryPlugin } from "@/configuration/plugins/sentry/sentry.configuration";
import { TTimezoneWorkerEvent } from "@/constants/timezone-worker.type";
import { createV4hPinia } from "@/core/pinia/pinia";
import detectGPU from "@/detect-gpu";
import { getMixpanelConsentOnDidomiReady, onDidomiConsentChange } from "@/didomi";
import highchartsPlugin from "@/highcharts";
import "@/assets/styles/index.scss";
import i18n from "@/i18n";
import { setPrimeVuePrimaryColor, usePrimeVuePlugin } from "@/primevue";
import router from "@/router/router";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";
import { useConfigurationStore } from "@/stores/configuration/configuration-store";
import { initializeEnv } from "@/utils/env";
import { useVueQuery } from "@/vue-query";

import FocusTrap from "primevue/focustrap";
import "primeicons/primeicons.css";
import ToastService from "primevue/toastservice";
import Tooltip from "primevue/tooltip";

import App from "@/App.vue";

const env = initializeEnv();

const services: IServices = createServices({ env });
const app = createApp(App);

app.use(provideServicesPlugin, { services });

app.use(createV4hPinia());
app.use(router);
app.use(usePrimeVuePlugin());
app.use(i18n);
app.use(ToastService);
app.use(useVueQuery());
app.use(highchartsPlugin);
app.use(enterpriseSentryPlugin, env);

app.directive("tooltip", Tooltip);
app.directive("focustrap", FocusTrap);

setPrimeVuePrimaryColor();
detectGPU();
enableMapSet();

app.mount("#app");

const worker = new Worker("/workers/timezone-worker.js");
worker.onmessage = (e: MessageEvent<TTimezoneWorkerEvent>) => {
  const store = useConfigurationStore();
  store.updateClientTimezone(e.data.currentTimezone);
};

MixpanelService.initialize(env);

onDidomiConsentChange(async () => {
  const hasUserAllowedTracking = await getMixpanelConsentOnDidomiReady();
  MixpanelService.handleTrackingConsent(hasUserAllowedTracking);
});
